import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { IconArrowLeft, IconChecks, IconLoader2 } from "@tabler/icons-react"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useGetTeamsQuery } from "@/app/booking-calendar/bookingCalendarApi"
import { teamIdForSlug } from "@/app/booking-calendar/lib/rescheduleTargets"
import {
  type CalendarDate,
  SITE_TIME_ZONE,
  todayInZone,
  zonedDateAndMinutes,
} from "@/app/booking-calendar/lib/siteTime"
import { weekdayInView } from "@/app/booking-calendar/lib/staffDay"
import { BookingMoveBatchProgressView } from "../../../components/BookingMoveBatchProgress"
import { customerNames, describeResolveError } from "../../../components/automaticReassignment"
import {
  type BookingMoveBatch,
  type ManualResolveErrors,
  useGetResolveOptionsQuery,
  useGetScheduleOverrideQuery,
  useRunManualResolutionMutation,
} from "../../../schedule-overrides/scheduleOverridesApi"
import { useBookingMoveBatch } from "../../../schedule-overrides/lib/useBookingMoveBatch"
import { describeTarget, formatWindow, kindLabel } from "../../../schedule-overrides/lib/overrideForm"
import {
  activeBookingOf,
  clickBooking,
  EMPTY_PAIRING_STATE,
  hasPairings,
  keepListed,
  pair,
  pairingCount,
  pairingsByHostDayOf,
  type Pairing,
  type PairingState,
  toSubmission,
  unpair,
} from "../lib/pairingState"
import { ResolveBookingList } from "./ResolveBookingList"
import { ResolveDayGrid } from "./ResolveDayGrid"
import { UnappliedChangesGuard } from "./UnappliedChangesGuard"

const NOT_AVAILABLE = "Manual reschedule is not available for this override."

/** Override statuses (AmsScheduleOverrideStatus) that are withdrawn, wholly (3) or in part (4): nothing may be paired. */
const WITHDRAWN_STATUSES: readonly number[] = [3, 4]

/** "Not available", for an override the server does not know (404) or can no longer resolve. */
export function ManualRescheduleUnavailable({ message = NOT_AVAILABLE }: { message?: string }) {
  return (
    <Alert data-testid="manual-reschedule-unavailable">
      <AlertTitle>Not available</AlertTitle>
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  )
}

/** The date the grid opens on: the earliest affected booking's, or today when that has passed; a weekday. */
export function initialDate(starts: readonly string[], today: CalendarDate, timeZone: string = SITE_TIME_ZONE): CalendarDate {
  const dates = starts.map(start => zonedDateAndMinutes(start, timeZone).date).sort()
  const first = dates[0]
  return weekdayInView(first && first > today ? first : today)
}

/** The 400 body's reasons by booking request id (lower case), or null for any other refusal. */
export function pairingErrorsOf(error: unknown): Record<string, string> | null {
  const { status, data } = (error ?? {}) as { status?: unknown; data?: unknown }
  if (status !== 400 || !data || typeof data !== "object") return null
  const errors = (data as Partial<ManualResolveErrors>).errors
  if (!Array.isArray(errors)) return null
  const byId: Record<string, string> = {}
  for (const e of errors) {
    if (e && typeof e.bookingRequestId === "string" && typeof e.reason === "string") byId[e.bookingRequestId.toLowerCase()] = e.reason
  }
  return byId
}

/**
 * The manual reschedule workspace for one override (AB#811, SU10). Left: its affected bookings still
 * listed. Right: a site's day, judged for the active booking. Pairings live here only (ams-1rd), are
 * applied together as one Manual batch, and are asked about before leaving.
 */
export function ManualRescheduleWorkspace({ overrideId }: { overrideId: string }) {
  const timeZone = SITE_TIME_ZONE
  const optionsQuery = useGetResolveOptionsQuery(overrideId, { refetchOnMountOrArgChange: true })
  const detailQuery = useGetScheduleOverrideQuery(overrideId)
  const teamsQuery = useGetTeamsQuery()
  const [run, runState] = useRunManualResolutionMutation()

  const [state, setState] = useState<PairingState>(EMPTY_PAIRING_STATE)
  const [date, setDate] = useState<CalendarDate | null>(null)
  const [siteSlug, setSiteSlug] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [message, setMessage] = useState<string | null>(null)
  const [batchId, setBatchId] = useState<string | null>(null)
  const [names, setNames] = useState<ReadonlyMap<string, string>>(new Map())
  const [finished, setFinished] = useState<BookingMoveBatch | null>(null)

  const detail = detailQuery.currentData ?? detailQuery.data
  const override = detail?.override
  const bookings = useMemo(() => detail?.bookings ?? [], [detail])
  const options = optionsQuery.currentData

  // Once the bookings are in: the day and site the grid opens on.
  useEffect(() => {
    if (!detail) return
    const list = detail.bookings ?? []
    setDate(current => current ?? initialDate(list.map(b => b.start), todayInZone(timeZone), timeZone))
    setSiteSlug(current => current
      ?? list.find(b => !!b.teamSlug)?.teamSlug
      ?? detail.override.teamSlug
      ?? detail.override.affectedTeamSlugs[0]
      ?? null)
  }, [detail, timeZone])

  // A booking that has left the list (moved, or dealt with elsewhere) leaves the pairings too.
  const listedIds = useMemo(() => bookings.flatMap(b => (b.requestId ? [b.requestId] : [])), [bookings])
  useEffect(() => {
    if (!detail) return
    setState(current => keepListed(current, listedIds))
  }, [detail, listedIds])

  // A batch already under way for this override, from before the page opened: followed.
  useEffect(() => {
    if (options?.activeBatchId && batchId === null && detail) {
      setNames(customerNames(bookings))
      setBatchId(options.activeBatchId)
    }
  }, [options?.activeBatchId, batchId, bookings, detail])

  const batch = useBookingMoveBatch(batchId)
  useEffect(() => {
    if (batch.batch && (batch.status === "succeeded" || batch.status === "failed")) setFinished(batch.batch)
  }, [batch.batch, batch.status])

  const failures = useMemo(() => {
    const byId: Record<string, string> = {}
    for (const item of finished?.items ?? []) {
      if (item.status === 2) byId[item.bookingRequestId.toLowerCase()] = item.failureDetail ?? ""
    }
    return byId
  }, [finished])

  const activeDetails = bookings.find(b => !!b.requestId && b.requestId === state.activeId)
  const active = activeDetails ? activeBookingOf(activeDetails) : null
  const placed = useMemo(() => pairingsByHostDayOf(state, timeZone), [state, timeZone])
  const teams = teamsQuery.currentData ?? []
  const teamId = teamIdForSlug(teams, siteSlug)
  const sitesListed = new Set(bookings.map(b => b.teamSlug).filter(Boolean))

  const running = batch.isRunning
  // Withdrawn, wholly or in part: the server refuses manual for it, so nothing may be paired either.
  const withdrawn = !!override && WITHDRAWN_STATUSES.includes(override.status)
  const pairingLocked = running || withdrawn
  const manualRefusal = options && !options.manual.enabled && !running ? options.manual.reason : null
  const count = pairingCount(state)
  const canApply = count > 0 && !pairingLocked && !runState.isLoading && !manualRefusal

  if ((optionsQuery.error as { status?: number } | undefined)?.status === 404) return <ManualRescheduleUnavailable />
  if ((detailQuery.error as { status?: number } | undefined)?.status === 404) {
    return <ManualRescheduleUnavailable message="This override could not be found." />
  }

  function clickRow(requestId: string) {
    const booking = bookings.find(b => b.requestId === requestId)
    setState(current => clickBooking(current, requestId))
    setErrors(current => dropKey(current, requestId))
    // The grid follows the booking being placed to its own site (a move keeps the site, ams-2db).
    if (booking?.teamSlug && state.activeId !== requestId) setSiteSlug(booking.teamSlug)
  }

  function pairTarget(requestId: string, target: Pairing) {
    setState(current => pair(current, requestId, target))
    setErrors(current => dropKey(current, requestId))
  }

  function unpairTarget(requestId: string) {
    setState(current => unpair(current, requestId))
    setErrors(current => dropKey(current, requestId))
  }

  async function apply() {
    if (!canApply) return
    setMessage(null)
    const result = await run({ overrideId, pairings: toSubmission(state) })
    if ("error" in result) {
      const refused = pairingErrorsOf(result.error)
      if (refused) {
        setErrors(refused)
        // A reason for a booking no longer listed has no row to sit by: said here instead.
        const listed = new Set(listedIds.map(id => id.toLowerCase()))
        const loose = Object.entries(refused).filter(([id]) => !listed.has(id)).map(([, reason]) => reason)
        if (Object.keys(refused).length === 0) setMessage(describeResolveError(result.error, "Manual reschedule"))
        else if (loose.length > 0) setMessage(loose.join(" "))
      } else {
        setErrors({})
        setMessage(describeResolveError(result.error, "Manual reschedule"))
      }
      return
    }
    setNames(customerNames(bookings))
    setFinished(null)
    setErrors({})
    setState(EMPTY_PAIRING_STATE)
    setBatchId(result.data.batchId)
  }

  const loading = detailQuery.isLoading || (!detail && detailQuery.isFetching)

  return (
    <div className="flex flex-col gap-4">
      <UnappliedChangesGuard when={hasPairings(state)} count={count} />

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Button asChild variant="ghost" size="sm">
          <Link to={override?.teamSlug ? `/appointments/availability?site=${encodeURIComponent(override.teamSlug)}` : "/appointments/availability"}>
            <IconArrowLeft className="size-4" />
            Availability
          </Link>
        </Button>
        {override && (
          <span className="text-muted-foreground" data-testid="resolve-override">
            {kindLabel(override.kind)} — {describeTarget(override)},{" "}
            {formatWindow(override.startDate, override.startTime, override.endDate, override.endTime)}
          </span>
        )}
      </div>

      {detailQuery.isError && !detail && (
        <p role="alert" className="text-sm text-destructive">This override could not be loaded. Try again in a moment.</p>
      )}
      {override?.status === 3 && (
        <ManualRescheduleUnavailable message="This override has been withdrawn, so its appointments are no longer affected." />
      )}
      {override?.status === 4 && (
        <ManualRescheduleUnavailable message="This override has been partly withdrawn, so its appointments can no longer be moved from here." />
      )}

      <div className="grid gap-4 lg:grid-cols-[22rem_minmax(0,1fr)]">
        <section className="flex flex-col gap-3" aria-label="Appointments to move">
          <h2 className="text-base font-semibold">Affected appointments</h2>
          <p className="text-xs text-muted-foreground">
            Select an appointment, then a slot marked Move here. The appointment stays at its site; a slot of
            another type there changes its type. Select a paired appointment, or its pending slot, to undo.
          </p>
          {loading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : (
            <ResolveBookingList
              bookings={bookings}
              state={state}
              errors={errors}
              failures={failures}
              timeZone={timeZone}
              disabled={pairingLocked}
              showSite={sitesListed.size > 1}
              onClick={clickRow}
            />
          )}

          <div className="flex flex-col gap-2 border-t pt-3">
            <Button onClick={() => void apply()} disabled={!canApply} data-testid="apply-changes">
              {runState.isLoading ? <IconLoader2 className="size-4 animate-spin" /> : <IconChecks className="size-4" />}
              Apply changes{count > 0 ? ` (${count})` : ""}
            </Button>
            {manualRefusal && (
              <p className="text-xs text-muted-foreground" data-testid="manual-refusal">{manualRefusal}</p>
            )}
            {message && <p role="alert" className="text-sm text-destructive" data-testid="apply-error">{message}</p>}
          </div>

          {batchId && (
            <div className="rounded-lg border p-3" data-testid="manual-batch">
              <BookingMoveBatchProgressView batch={batch.batch} names={names} notFound={batch.notFound} error={batch.error} />
            </div>
          )}
        </section>

        <section className="min-w-0" aria-label="Day">
          {!siteSlug || !date ? (
            loading ? <Skeleton className="h-64 w-full" /> : <p className="text-sm text-muted-foreground">No site to show.</p>
          ) : teamId === null ? (
            teamsQuery.isLoading || teamsQuery.isFetching
              ? <Skeleton className="h-64 w-full" data-testid="resolve-grid-waiting" />
              : <p className="text-sm text-muted-foreground">The scheduling system has no site {siteSlug}.</p>
          ) : (
            <ResolveDayGrid
              overrideId={overrideId}
              teamId={teamId}
              teamSlug={siteSlug}
              date={date}
              onDateChange={setDate}
              active={pairingLocked ? null : active}
              placed={placed}
              onPair={pairTarget}
              onUnpair={unpairTarget}
              disabled={pairingLocked}
            />
          )}
        </section>
      </div>
    </div>
  )
}

function dropKey(record: Record<string, string>, requestId: string): Record<string, string> {
  const key = requestId.toLowerCase()
  if (!(key in record)) return record
  return Object.fromEntries(Object.entries(record).filter(([k]) => k !== key))
}
